import { getAuthToken, getAuthUser } from '@/lib/api/token-store';
import { getThreadMembershipChangeDetail } from '@/lib/realtime/chat-message-helpers';
import { isAppointmentRealtimePayload } from '@/lib/realtime/appointment-realtime-helpers';
import { ReconnectingChatWebSocket } from '@/lib/realtime/reconnecting-chat-ws';
import { conversationContentTypeFromMime } from '@/lib/realtime/message-attachments';
import { applyPresenceRealtime } from '@/lib/realtime/presence-service';
import { applyTypingRealtime } from '@/lib/realtime/typing-store';
import {
  flushDeliveredAcks,
  resetDeliveredAckQueue,
  setDeliveredAckSender,
} from '@/lib/realtime/delivery-receipt-queue';

let socketClient = null;
let connectionStatus = 'disconnected';
const APPOINTMENTS_WILDCARD_SUBJECT = 'appointments.*';
const USER_OBJECT_SUBJECT_PREFIX = 'system.object.user.';

/** Whether `subscribe` with kind `me` was sent on the current socket (once per connection). */
let meSubscribedOnCurrentSocket = false;
let appointmentsSubscribedOnCurrentSocket = false;
let userThreadsSubscribedOnCurrentSocket = false;
let userObjectSubscribedOnCurrentSocket = false;
let queueTenantSubscribedOnCurrentSocket = false;
let workPlanSubscribedOnCurrentSocket = false;
let systemGlobalSubscribedOnCurrentSocket = false;

const statusListeners = new Set();
const messageListeners = new Set();
const appointmentListeners = new Set();
const userThreadListeners = new Set();
const userObjectListeners = new Set();
const queueListeners = new Set();
const receiptListeners = new Set();
const workPlanListeners = new Set();
const dashboardPostsListeners = new Set();
const botServiceHoursListeners = new Set();
const userNotesListeners = new Set();

function toTrimmedString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function toObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function parseJsonObject(value) {
  if (typeof value !== 'string') {
    return toObject(value);
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  try {
    return toObject(JSON.parse(trimmed));
  } catch {
    return null;
  }
}

function pickString(...values) {
  for (const value of values) {
    const next = toTrimmedString(value);
    if (next) {
      return next;
    }
  }
  return '';
}

function readMetadataObjectId(metadata) {
  const normalizedMetadata = parseJsonObject(metadata);
  if (!normalizedMetadata) {
    return '';
  }
  return pickString(
    normalizedMetadata.objectId,
    normalizedMetadata.objectID,
    normalizedMetadata.object_id,
  );
}

/**
 * Classifies the PresenceHub's direct pushes: presence deltas, the connect snapshot, and
 * typing relays. MUST run before normalizeUserObjectRealtimePayload — presence deltas
 * match its system-envelope sniff and would trigger user-profile refetches on every
 * connect/disconnect, and a snapshot (whose userId is the recipient) would refresh the
 * wrong cache entry.
 */
function normalizePresenceRealtimePayload(rawPayload) {
  const root = toObject(rawPayload);
  if (!root) {
    return null;
  }
  const message = toObject(root.message) || parseJsonObject(root.message);
  const messageType = pickString(message?.type, message?.kind).toLowerCase();
  if (messageType !== 'system') {
    return null;
  }
  const body = parseJsonObject(message?.body);
  const action = pickString(body?.action).toLowerCase();
  if (action === 'presence') {
    const userId = pickString(body?.userId);
    if (!userId) {
      return null;
    }
    const updates = toObject(body?.updates) || {};
    return {
      kind: 'delta',
      userId,
      online: updates.online === true,
      availability: pickString(updates.availability),
    };
  }
  if (action === 'presence.snapshot') {
    const updates = toObject(body?.updates) || {};
    return {
      kind: 'snapshot',
      online: Array.isArray(updates.online) ? updates.online : [],
    };
  }
  if (action === 'typing') {
    const userId = pickString(body?.userId);
    const threadId = pickString(body?.threadId);
    if (!userId || !threadId) {
      return null;
    }
    return { kind: 'typing', userId, threadId };
  }
  return null;
}

function normalizeUserObjectRealtimePayload(rawPayload) {
  const root = toObject(rawPayload);
  if (!root) {
    return null;
  }
  const message = toObject(root.message) || parseJsonObject(root.message);
  const messageType = pickString(message?.type, message?.kind).toLowerCase();
  const parsedMessageBody = parseJsonObject(message?.body);
  /** NATS user-object publishes only `{ message }` with no subject; body matches UserObjectUpdateBody (userId, no threadId). */
  const bodyUserIdHint = pickString(parsedMessageBody?.userId, parsedMessageBody?.userID);
  const bodyThreadIdHint = pickString(parsedMessageBody?.threadId);
  const looksLikeSystemUserObjectEnvelope =
    messageType === 'system' && bodyUserIdHint && !bodyThreadIdHint;
  const subject = pickString(
    root.subject,
    root.topic,
    root.channel,
    root.subscription,
    root.event,
    message?.subject,
    message?.topic,
    message?.channel,
  );
  const lowerSubject = subject.toLowerCase();
  const metadata = parseJsonObject(root.metadata) || parseJsonObject(message?.metadata);
  const metadataObjectType = pickString(
    metadata?.objectType,
    metadata?.object_type,
  ).toLowerCase();
  const metadataSystemScope = pickString(
    metadata?.systemScope,
    metadata?.system_scope,
  ).toLowerCase();
  const looksLikeUserObjectSubject = lowerSubject.startsWith(USER_OBJECT_SUBJECT_PREFIX);
  const looksLikeUserObjectMetadata =
    metadataObjectType === 'user' && (!metadataSystemScope || metadataSystemScope === 'object');
  if (!looksLikeUserObjectSubject && !looksLikeUserObjectMetadata && !looksLikeSystemUserObjectEnvelope) {
    return null;
  }

  const parsedRootBody = parseJsonObject(root.body);
  const sources = [
    parseJsonObject(root.userObjectChange),
    parseJsonObject(root.user_change),
    parseJsonObject(root.user),
    parseJsonObject(root.change),
    parseJsonObject(root.payload),
    parsedRootBody,
    parseJsonObject(root.payload?.body),
    message,
    parsedMessageBody,
    parseJsonObject(message?.payload),
    parseJsonObject(message?.payload?.body),
  ].filter(Boolean);

  let action = '';
  let userId = '';
  let updates = null;
  for (const source of sources) {
    action ||= pickString(source?.action, source?.Action);
    userId ||= pickString(
      source?.userId,
      source?.userID,
      source?.UserID,
      source?.id,
    );
    if (!updates) {
      const normalizedUpdates = toObject(source?.updates);
      if (normalizedUpdates) {
        updates = normalizedUpdates;
      }
    }
  }

  if (!userId && looksLikeUserObjectSubject) {
    userId = subject.slice(USER_OBJECT_SUBJECT_PREFIX.length).trim();
  }
  userId ||= readMetadataObjectId(root.metadata);
  userId ||= readMetadataObjectId(message?.metadata);

  if (!action) {
    action = 'updated';
  }
  if (!userId && !updates) {
    return null;
  }
  return {
    action: action.toLowerCase(),
    userId,
    updates,
    subject,
    raw: rawPayload,
  };
}

function notifyStatus(next) {
  const previous = connectionStatus;
  connectionStatus = next;
  // Acks queued while the socket was down could not be sent (`send` throws when it is not OPEN),
  // so drain them the moment it comes back.
  if (next === 'connected' && previous !== 'connected') {
    flushDeliveredAcks();
  }
  for (const listener of statusListeners) {
    try {
      listener(next);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

function notifyMessage(detail) {
  for (const listener of messageListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

function notifyReceipts(detail) {
  for (const listener of receiptListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

/**
 * Recognises a receipt delta: a system envelope whose body is `{ action: "receipts", threadId,
 * entries[], resync }`. Returns null for anything else.
 *
 * Deliberately checked before the presence branch. Presence classifies on a body `userId`, which
 * a receipt delta does not carry, so the two cannot collide — but the ordering makes that a
 * property of this file rather than a coincidence.
 */
function normalizeReceiptRealtimePayload(payload) {
  const message = toObject(payload?.message);
  if (!message || message.type !== 'system') {
    return null;
  }
  const body = parseJsonObject(message.body);
  if (!body || body.action !== 'receipts') {
    return null;
  }
  const threadId = toTrimmedString(body.threadId);
  if (!threadId) {
    return null;
  }
  const entries = Array.isArray(body.entries) ? body.entries : [];
  return {
    threadId,
    resync: body.resync === true,
    entries: entries
      .map((entry) => ({
        messageId: toTrimmedString(entry?.messageId),
        deliveredBy: Array.isArray(entry?.deliveredBy) ? entry.deliveredBy.map(toTrimmedString).filter(Boolean) : [],
        readBy: Array.isArray(entry?.readBy) ? entry.readBy.map(toTrimmedString).filter(Boolean) : [],
        channelStatus: toObject(entry?.channelStatus),
      }))
      .filter((entry) => entry.messageId),
    at: toTrimmedString(body.at),
  };
}

function notifyAppointment(detail) {
  for (const listener of appointmentListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

function notifyUserThreadEvents(detail) {
  for (const listener of userThreadListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

function notifyUserObjectEvents(detail) {
  for (const listener of userObjectListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

function notifyQueueEvents(detail) {
  for (const listener of queueListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

/**
 * Patient-queue envelope on the queue.tenant.* feed (QueueChangeBody): a system
 * message whose body carries queueInstanceId (always serialized, no omitempty)
 * or an entry_* action. Returns the normalized change, or null for anything
 * else. Unrecognized actions still flow through — the documented client
 * contract is "treat unknown queue actions as refetch triggers".
 */
function normalizeQueueRealtimePayload(rawPayload) {
  const root = toObject(rawPayload);
  if (!root) {
    return null;
  }
  const message = toObject(root.message) || parseJsonObject(root.message);
  const messageType = pickString(message?.type, message?.kind).toLowerCase();
  if (messageType !== 'system') {
    return null;
  }
  const body = parseJsonObject(message?.body);
  if (!body) {
    return null;
  }
  const action = pickString(body.action, body.Action).toLowerCase();
  const hasQueueShape =
    'queueInstanceId' in body || 'queueDefinitionId' in body || action.startsWith('entry_');
  if (!hasQueueShape) {
    return null;
  }
  const entry = toObject(body.entry) || parseJsonObject(body.entry);
  return {
    action,
    queueInstanceId: pickString(body.queueInstanceId),
    tenantId: pickString(body.tenantId),
    clinicId: pickString(body.clinicId),
    date: pickString(body.date),
    patientId: pickString(body.patientId),
    entry,
    raw: rawPayload,
  };
}

/**
 * Work-plan envelope on the workplan.* feed (WorkPlanEventBody): a system message whose
 * body carries the fixed `workPlan: true` discriminator. Returns the normalized change
 * (postings_changed / day_published / mc_submitted / mc_decided), or null.
 */
function normalizeWorkPlanRealtimePayload(rawPayload) {
  const root = toObject(rawPayload);
  if (!root) {
    return null;
  }
  const message = toObject(root.message) || parseJsonObject(root.message);
  const messageType = pickString(message?.type, message?.kind).toLowerCase();
  if (messageType !== 'system') {
    return null;
  }
  const body = parseJsonObject(message?.body);
  if (!body || body.workPlan !== true) {
    return null;
  }
  return {
    action: pickString(body.action).toLowerCase(),
    dates: Array.isArray(body.dates) ? body.dates.filter((d) => typeof d === 'string') : [],
    mcRequestId: pickString(body.mcRequestId),
    mcStatus: pickString(body.mcStatus),
    pendingMcCount: Number.isFinite(Number(body.pendingMcCount)) && body.pendingMcCount !== null && body.pendingMcCount !== undefined
      ? Number(body.pendingMcCount)
      : null,
    raw: rawPayload,
  };
}

function notifyWorkPlanEvents(detail) {
  for (const listener of workPlanListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

/**
 * Dashboard-announcements ping on system.global (DashboardPostsEventBody): a system
 * message whose body carries the fixed `dashboardPosts: true` discriminator. Ids only —
 * the store refetches the feed through the authorized GET. Returns the normalized change
 * (created / updated / deleted), or null.
 */
function normalizeDashboardPostsRealtimePayload(rawPayload) {
  const root = toObject(rawPayload);
  if (!root) {
    return null;
  }
  const message = toObject(root.message) || parseJsonObject(root.message);
  const messageType = pickString(message?.type, message?.kind).toLowerCase();
  if (messageType !== 'system') {
    return null;
  }
  const body = parseJsonObject(message?.body);
  if (!body || body.dashboardPosts !== true) {
    return null;
  }
  return {
    action: pickString(body.action).toLowerCase(),
    postId: pickString(body.postId),
    raw: rawPayload,
  };
}

function notifyDashboardPostEvents(detail) {
  for (const listener of dashboardPostsListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

/**
 * "My Notes" scratchpad change ping (UserNotesEnvelope, pushed directly to the owner's
 * own sockets — never a broadcast feed): a system message whose body carries the fixed
 * `userNotes: true` discriminator. Ids only — the notes store refetches through the
 * authorized GET, so the transport never carries note content. Returns the normalized
 * change (created / updated / deleted), or null.
 */
function normalizeUserNotesRealtimePayload(rawPayload) {
  const root = toObject(rawPayload);
  if (!root) {
    return null;
  }
  const message = toObject(root.message) || parseJsonObject(root.message);
  const messageType = pickString(message?.type, message?.kind).toLowerCase();
  if (messageType !== 'system') {
    return null;
  }
  const body = parseJsonObject(message?.body);
  if (!body || body.userNotes !== true) {
    return null;
  }
  return {
    action: pickString(body.action).toLowerCase(),
    blockId: pickString(body.blockId),
    raw: rawPayload,
  };
}

function notifyUserNotesEvents(detail) {
  for (const listener of userNotesListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

/**
 * Bot working-hours settings-changed ping on system.global (BotServiceHoursEventBody): a
 * system message whose body carries the fixed `botServiceHours: true` discriminator. No
 * content — the open editor refetches through the authorized GET. Returns the normalized
 * change, or null.
 */
function normalizeBotServiceHoursRealtimePayload(rawPayload) {
  const root = toObject(rawPayload);
  if (!root) {
    return null;
  }
  const message = toObject(root.message) || parseJsonObject(root.message);
  const messageType = pickString(message?.type, message?.kind).toLowerCase();
  if (messageType !== 'system') {
    return null;
  }
  const body = parseJsonObject(message?.body);
  if (!body || body.botServiceHours !== true) {
    return null;
  }
  return {
    action: pickString(body.action).toLowerCase() || 'changed',
    raw: rawPayload,
  };
}

function notifyBotServiceHoursEvents(detail) {
  for (const listener of botServiceHoursListeners) {
    try {
      listener(detail);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

/**
 * Subscribes the current user to all conversation traffic (`kind: 'me'`).
 * Call once after each successful socket open / reconnect.
 */
function sendSubscribeMe() {
  if (!socketClient) {
    return;
  }
  if (meSubscribedOnCurrentSocket) {
    return;
  }
  try {
    socketClient.send({
      action: 'subscribe',
      subscribe: {
        kind: 'me',
      },
    });
    meSubscribedOnCurrentSocket = true;
  } catch (_error) {
    // ignore send failures; next reconnect will retry subscriptions
  }
}

function sendSubscribeAppointments() {
  if (!socketClient) {
    return;
  }
  if (appointmentsSubscribedOnCurrentSocket) {
    return;
  }
  try {
    socketClient.send({
      action: 'subscribe',
      subscribe: {
        kind: 'appointments',
        scope: 'all',
        subject: APPOINTMENTS_WILDCARD_SUBJECT,
        topic: APPOINTMENTS_WILDCARD_SUBJECT,
      },
    });
    appointmentsSubscribedOnCurrentSocket = true;
  } catch (_error) {
    // ignore send failures; next reconnect will retry subscriptions
  }
}

function sendSubscribeUserThreads() {
  if (!socketClient) {
    return;
  }
  if (userThreadsSubscribedOnCurrentSocket) {
    return;
  }
  try {
    socketClient.send({
      action: 'subscribe',
      subscribe: {
        kind: 'userThreads',
      },
    });
    userThreadsSubscribedOnCurrentSocket = true;
  } catch (_error) {
    // ignore send failures; next reconnect will retry subscriptions
  }
}

function sendSubscribeUserObject() {
  if (!socketClient) {
    return;
  }
  if (userObjectSubscribedOnCurrentSocket) {
    return;
  }
  const authUser = getAuthUser();
  const currentUserId = String(authUser?.id || '').trim();
  if (!currentUserId) {
    return;
  }
  const useWildcard = Boolean(authUser?.isEmployee);
  const objectId = useWildcard ? '*' : currentUserId;
  try {
    socketClient.send({
      action: 'subscribe',
      subscribe: {
        kind: 'system',
        scope: 'object',
        objectType: 'user',
        objectId,
      },
    });
    userObjectSubscribedOnCurrentSocket = true;
  } catch (_error) {
    // ignore send failures; next reconnect will retry subscriptions
  }
}

function sendSubscribeQueueTenant() {
  if (!socketClient) {
    return;
  }
  if (queueTenantSubscribedOnCurrentSocket) {
    return;
  }
  // Staff only: the server resolves the subject tenant from the caller's user
  // doc; patients get their queue state via user.queue.* instead.
  if (!getAuthUser()?.isEmployee) {
    return;
  }
  try {
    socketClient.send({
      action: 'subscribe',
      subscribe: {
        kind: 'queueTenant',
      },
    });
    queueTenantSubscribedOnCurrentSocket = true;
  } catch (_error) {
    // ignore send failures; next reconnect will retry subscriptions
  }
}

function sendSubscribeWorkPlan() {
  if (!socketClient) {
    return;
  }
  if (workPlanSubscribedOnCurrentSocket) {
    return;
  }
  // Staff only: the server rejects the workPlan kind for non-employees.
  if (!getAuthUser()?.isEmployee) {
    return;
  }
  try {
    socketClient.send({
      action: 'subscribe',
      subscribe: {
        kind: 'workPlan',
      },
    });
    workPlanSubscribedOnCurrentSocket = true;
  } catch (_error) {
    // ignore send failures; next reconnect will retry subscriptions
  }
}

// The system.global feed carries the dashboard-announcements ping. The server does not
// gate this kind, but only employee sessions have any use for it — keep the guard for
// symmetry with the other staff-wide subscriptions.
function sendSubscribeSystemGlobal() {
  if (!socketClient) {
    return;
  }
  if (systemGlobalSubscribedOnCurrentSocket) {
    return;
  }
  if (!getAuthUser()?.isEmployee) {
    return;
  }
  try {
    socketClient.send({
      action: 'subscribe',
      subscribe: {
        kind: 'system',
        scope: 'global',
      },
    });
    systemGlobalSubscribedOnCurrentSocket = true;
  } catch (_error) {
    // ignore send failures; next reconnect will retry subscriptions
  }
}

function resubscribeAfterConnect() {
  meSubscribedOnCurrentSocket = false;
  appointmentsSubscribedOnCurrentSocket = false;
  userThreadsSubscribedOnCurrentSocket = false;
  userObjectSubscribedOnCurrentSocket = false;
  queueTenantSubscribedOnCurrentSocket = false;
  workPlanSubscribedOnCurrentSocket = false;
  systemGlobalSubscribedOnCurrentSocket = false;
  sendSubscribeMe();
  sendSubscribeAppointments();
  sendSubscribeUserThreads();
  sendSubscribeUserObject();
  sendSubscribeQueueTenant();
  sendSubscribeWorkPlan();
  sendSubscribeSystemGlobal();
}

export function getChatConnectionStatus() {
  return connectionStatus;
}

export function subscribeChatConnectionStatus(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  statusListeners.add(listener);
  listener(connectionStatus);
  return () => {
    statusListeners.delete(listener);
  };
}

export function subscribeChatMessages(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  messageListeners.add(listener);
  return () => {
    messageListeners.delete(listener);
  };
}

/**
 * Subscribes to delivered/seen receipt deltas.
 *
 * These are pushed to every socket of a thread member regardless of what it subscribed to, so no
 * `subscribe` frame is needed — being a thread member is the only requirement.
 */
export function subscribeChatReceipts(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  receiptListeners.add(listener);
  return () => {
    receiptListeners.delete(listener);
  };
}

export function subscribeAppointmentEvents(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  appointmentListeners.add(listener);
  return () => {
    appointmentListeners.delete(listener);
  };
}

/** @param {(detail: { action: string, threadId: string }) => void} listener */
export function subscribeUserThreadEvents(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  userThreadListeners.add(listener);
  return () => {
    userThreadListeners.delete(listener);
  };
}

/** @param {(detail: { action: string, userId: string, updates?: Record<string, unknown> | null, subject: string }) => void} listener */
export function subscribeUserObjectEvents(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  userObjectListeners.add(listener);
  return () => {
    userObjectListeners.delete(listener);
  };
}

/**
 * Patient-queue changes on the caller-tenant's feed (check-ins, board moves,
 * appointment_changed pings). Employee sessions only — the queueTenant
 * subscription is skipped for patients.
 * @param {(detail: { action: string, queueInstanceId: string, tenantId: string, clinicId: string, date: string, patientId: string, entry: Record<string, unknown> | null }) => void} listener
 */
export function subscribeQueueEvents(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  queueListeners.add(listener);
  return () => {
    queueListeners.delete(listener);
  };
}

/**
 * Work-plan roster / MC changes on the staff-wide workplan.* feed. Employee sessions
 * only — the subscription is skipped for patients.
 * @param {(detail: { action: string, dates: string[], mcRequestId: string, mcStatus: string, pendingMcCount: number | null }) => void} listener
 */
export function subscribeWorkPlanEvents(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  workPlanListeners.add(listener);
  return () => {
    workPlanListeners.delete(listener);
  };
}

/**
 * Dashboard-announcements changes on the system.global feed (ids-only refetch pings).
 * Employee sessions only — the subscription is skipped for patients.
 * @param {(detail: { action: string, postId: string }) => void} listener
 */
export function subscribeDashboardPostEvents(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  dashboardPostsListeners.add(listener);
  return () => {
    dashboardPostsListeners.delete(listener);
  };
}

/**
 * "My Notes" scratchpad changes, pushed directly to this user's own sockets (ids-only
 * refetch pings; the store reloads through the authorized GET).
 * @param {(detail: { action: string, blockId: string }) => void} listener
 */
export function subscribeUserNotesEvents(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  userNotesListeners.add(listener);
  return () => {
    userNotesListeners.delete(listener);
  };
}

/**
 * Bot working-hours settings changes on the system.global feed (content-free refetch
 * pings — the editor reloads through the authorized GET).
 * @param {(detail: { action: string }) => void} listener
 */
export function subscribeBotServiceHoursEvents(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  botServiceHoursListeners.add(listener);
  return () => {
    botServiceHoursListeners.delete(listener);
  };
}

// Ids per receipt frame. The backend rejects anything above 500; staying well under keeps a
// single frame small enough not to matter.
const RECEIPT_CHUNK_SIZE = 200;

/**
 * Sends one receipt frame. `action` is "read" or "delivered" — the payloads are identical.
 * Passing threadId lets the server skip a lookup; without it, it derives the thread from the
 * first message and still enforces membership.
 */
function sendReceiptFrame(action, messageIds, threadId) {
  const ids = [...new Set((messageIds || []).map((x) => String(x || '').trim()).filter(Boolean))];
  if (!ids.length || !socketClient || connectionStatus !== 'connected') {
    return false;
  }
  const tid = String(threadId || '').trim();
  let sentAll = true;
  for (let i = 0; i < ids.length; i += RECEIPT_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + RECEIPT_CHUNK_SIZE);
    const payload = { messageIds: chunk };
    if (tid) {
      payload.threadId = tid;
    }
    try {
      socketClient.send({ action, [action]: payload });
    } catch (_error) {
      sentAll = false;
    }
  }
  return sentAll;
}

/**
 * Confirms read for a single message (backend: action "read").
 */
export function sendReadReceipt(messageId, threadId) {
  sendReceiptFrame('read', [messageId], threadId);
}

/**
 * Sends read receipts for many ids.
 *
 * One frame per chunk rather than one frame per message: the backend accepts `messageIds`, so a
 * thread-open sweep of 80 messages is a single frame instead of 80. That also removes the reason
 * the old implementation spread sends across animation frames — there is no longer a burst to
 * spread. Kept async so existing `await` call sites are unaffected.
 */
export function sendReadReceipts(messageIds, threadId) {
  sendReceiptFrame('read', messageIds, threadId);
  return Promise.resolve();
}

/**
 * Acks DELIVERY for messages this client picked up itself (i.e. from history after being
 * offline). Messages arriving over a live socket are stamped server-side and need no ack.
 */
export function sendDeliveredAcks(messageIds, threadId) {
  return sendReceiptFrame('delivered', messageIds, threadId);
}

// The queue is transport-agnostic; this is where it learns how to send.
setDeliveredAckSender(sendDeliveredAcks);

// Typing pings are throttled per thread: the receiver's indicator TTL (~6s in
// typing-store) outlives the resend interval, so continuous typing reads as continuous.
const TYPING_SEND_THROTTLE_MS = 3000;
const lastTypingSentAtByThread = new Map();

/**
 * Tells the other members of a thread that the caller is typing (best-effort,
 * ephemeral — dropped silently when the socket is down).
 */
export function sendTyping(threadId) {
  const id = String(threadId || '').trim();
  if (!id || !socketClient || connectionStatus !== 'connected') {
    return;
  }
  const now = Date.now();
  if (now - (lastTypingSentAtByThread.get(id) || 0) < TYPING_SEND_THROTTLE_MS) {
    return;
  }
  lastTypingSentAtByThread.set(id, now);
  try {
    socketClient.send({ action: 'typing', typing: { threadId: id } });
  } catch (_error) {
    // ignore
  }
}

/**
 * @param {string} threadId
 * @param {string} text
 * @param {{
 *   attachments?: Array<{ mimeType: string, data: string, filename: string }>,
 *   sender?: { userId: string, kind?: string },
 *   metadata?: Record<string, unknown>,
 * }} [options]
 */
export function sendConversationMessage(threadId, text, options = {}) {
  const trimmed = String(text || '').trim();
  const normalizedThreadId = String(threadId || '').trim();
  const rawAttachments = Array.isArray(options.attachments) ? options.attachments : [];
  const attachments = rawAttachments
    .map((a) => ({
      mimeType: String(a?.mimeType || 'application/octet-stream'),
      data: String(a?.data || ''),
      filename: String(a?.filename || 'upload'),
    }))
    .filter((a) => a.data.trim());

  if (!normalizedThreadId || (!trimmed && !attachments.length)) {
    throw new Error('threadId and message text or at least one attachment are required');
  }
  if (!socketClient) {
    throw new Error('WebSocket client is not running');
  }

  /** @type {Record<string, unknown>} */
  const messagePayload = {
    type: 'conversation',
    threadId: normalizedThreadId,
    contentType: 'text',
    body: {
      text: trimmed,
    },
  };

  if (attachments.length) {
    messagePayload.attachments = attachments;
    messagePayload.contentType = conversationContentTypeFromMime(attachments[0].mimeType);
  }

  const senderOpt = options.sender && typeof options.sender === 'object' ? options.sender : null;
  if (senderOpt) {
    const uid = String(senderOpt.userId || '').trim();
    if (uid) {
      const kindRaw = String(senderOpt.kind || 'user').toLowerCase();
      messagePayload.sender = {
        userId: uid,
        kind: kindRaw === 'bot' ? 'bot' : 'user',
      };
    }
  }
  const metaOpt = options.metadata;
  if (metaOpt && typeof metaOpt === 'object' && !Array.isArray(metaOpt)) {
    messagePayload.metadata = metaOpt;
  }

  socketClient.send({
    action: 'send',
    message: messagePayload,
  });
}

export function startChatRealtime() {
  if (socketClient) {
    return;
  }
  if (!String(getAuthToken() || '').trim()) {
    notifyStatus('disconnected');
    return;
  }

  socketClient = new ReconnectingChatWebSocket({
    getToken: () => getAuthToken(),
    onStatus: (status) => notifyStatus(status),
    onOpen: () => {
      resubscribeAfterConnect();
    },
    onJsonMessage: (payload) => {
      if (payload.type === 'error') {
        notifyMessage({
          kind: 'error',
          message: typeof payload.message === 'string' ? payload.message : 'unknown error',
        });
        return;
      }
      if (isAppointmentRealtimePayload(payload)) {
        notifyAppointment(payload);
        return;
      }
      // Work-plan envelopes are system messages with a fixed body discriminator —
      // classify them before the queue/user-object/message fallthroughs.
      const workPlanChange = normalizeWorkPlanRealtimePayload(payload);
      if (workPlanChange) {
        notifyWorkPlanEvents(workPlanChange);
        return;
      }
      // Dashboard-announcement pings are system envelopes too (system.global feed).
      const dashboardPostsChange = normalizeDashboardPostsRealtimePayload(payload);
      if (dashboardPostsChange) {
        notifyDashboardPostEvents(dashboardPostsChange);
        return;
      }
      // Bot working-hours settings-changed pings (system.global feed).
      const botServiceHoursChange = normalizeBotServiceHoursRealtimePayload(payload);
      if (botServiceHoursChange) {
        notifyBotServiceHoursEvents(botServiceHoursChange);
        return;
      }
      // "My Notes" change pings (pushed directly to this user's sockets).
      const userNotesChange = normalizeUserNotesRealtimePayload(payload);
      if (userNotesChange) {
        notifyUserNotesEvents(userNotesChange);
        return;
      }
      // Queue envelopes are system messages too — classify them before the
      // user-object/message fallthroughs or they'd render as chat noise.
      const queueChange = normalizeQueueRealtimePayload(payload);
      if (queueChange) {
        notifyQueueEvents(queueChange);
        return;
      }
      const receipts = normalizeReceiptRealtimePayload(payload);
      if (receipts) {
        notifyReceipts(receipts);
        return;
      }
      const presenceEvent = normalizePresenceRealtimePayload(payload);
      if (presenceEvent) {
        if (presenceEvent.kind === 'typing') {
          applyTypingRealtime(presenceEvent);
        } else {
          applyPresenceRealtime(presenceEvent);
        }
        return;
      }
      const userObjectChange = normalizeUserObjectRealtimePayload(payload);
      if (userObjectChange) {
        notifyUserObjectEvents(userObjectChange);
        return;
      }
      if (payload.message) {
        const membership = getThreadMembershipChangeDetail(payload.message);
        if (membership) {
          notifyUserThreadEvents(membership);
          return;
        }
        // Every NATS control envelope (`message.type === 'system'`) is classified above by its
        // body discriminator. One this build does not recognise is a NEWER server's ping, not a
        // chat message — surfacing it as one would toast raw JSON at the user (that exact bug
        // shipped once with the botServiceHours ping). Log and drop instead.
        const fallthroughType = pickString(toObject(payload.message)?.type).toLowerCase();
        if (fallthroughType === 'system') {
          console.debug('[realtime] Ignoring unrecognized system envelope', payload.message?.body);
          return;
        }
        notifyMessage({ kind: 'message', message: payload.message, update: payload.update === true });
      }
    },
  });

  socketClient.connect();
}

export function stopChatRealtime() {
  // Logout / profile switch: the next user must not inherit this one's dedupe set, or messages
  // they genuinely received would never be acked.
  resetDeliveredAckQueue();
  meSubscribedOnCurrentSocket = false;
  appointmentsSubscribedOnCurrentSocket = false;
  userThreadsSubscribedOnCurrentSocket = false;
  userObjectSubscribedOnCurrentSocket = false;
  queueTenantSubscribedOnCurrentSocket = false;
  workPlanSubscribedOnCurrentSocket = false;
  systemGlobalSubscribedOnCurrentSocket = false;
  if (socketClient) {
    socketClient.disconnect();
    socketClient = null;
  }
  notifyStatus('disconnected');
}
